/**
 * https://github.com/toss/slash/tree/main/packages/react/use-funnel
 *
 * MIT License
 *
 * Copyright (c) 2021 Viva Republica, Inc
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EventEmitter from 'eventemitter3';
import deepEqual from 'lodash-es/isEqual';
import { assert } from '@zep/utils';
import { Funnel, FunnelProps, NonEmptyArray, Step, StepProps } from './Funnel';
interface SetStepOptions {
  stepChangeType?: 'push' | 'replace';
  preserveQuery?: boolean;
}
type PublicFunnelProps<Steps extends NonEmptyArray<string>> = Omit<FunnelProps<Steps>, 'steps' | 'step'>;
type FunnelComponent<Steps extends NonEmptyArray<string>> = ((props: PublicFunnelProps<Steps>) => React.JSX.Element) & {
  Step: (props: StepProps<Steps>) => JSX.Element;
};
export const useFunnel = <Steps extends NonEmptyArray<string>,>(steps: Steps, options?: {
  initialStep?: Steps[number];
  onStepChange?: (name: Steps[number]) => void;
}): readonly [FunnelComponent<Steps>, (step: Steps[number], options?: SetStepOptions) => void] & {
  withState: <StateExcludeStep extends Record<string, unknown>>(initialState: StateExcludeStep) => [FunnelComponent<Steps>, StateExcludeStep, (next: Partial<StateExcludeStep & {
    step: Steps[number];
  }> | ((next: Partial<StateExcludeStep & {
    step: Steps[number];
  }>) => StateExcludeStep & {
    step: Steps[number];
  })) => void];
} => {
  assert(steps.length > 0, 'steps가 비어있습니다.');
  const [stepEventEmitter] = useState(() => new EventEmitter());
  const FunnelComponent = useMemo(() => Object.assign(function PublicFunnel(props: PublicFunnelProps<Steps>) {
    const [step, setStep] = useState(options?.initialStep ?? steps[0]);
    useEffect(() => {
      stepEventEmitter.on('step', setStep);
      return () => {
        stepEventEmitter.off('step', setStep);
      };
    }, []);
    return <Funnel<Steps> steps={steps} step={step} {...props} />;
  }, {
    Step
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const setStep = useCallback((step: Steps[number]) => {
    stepEventEmitter.emit('step', step);
    options?.onStepChange?.(step);
  }, [options, stepEventEmitter]);

  /**
   * 아래부터 withState() 구현입니다.
   * 외부 함수로 분리하기 어려워 당장은 inline 해둡니다.
   * FIXME: @junyong-lee withState() 구현을 외부 함수로 분리하기
   */

  type S = Record<string, unknown>;
  const [state, _setState] = useState<S>({});
  type Step = Steps[number];
  type NextState = S & {
    step?: Step;
  };
  const nextPendingStepRef = useRef<Step | null>(null);
  const nextStateRef = useRef<Partial<S> | null>(null);
  const setState = useCallback((next: Partial<NextState> | ((next: Partial<NextState>) => NextState)) => {
    let nextStepValue: Partial<NextState>;
    if (typeof next === 'function') {
      nextStepValue = next(state);
    } else {
      nextStepValue = next;
    }
    if (nextStepValue.step != null) {
      nextPendingStepRef.current = nextStepValue.step;
    }
    nextStateRef.current = nextStepValue;
    _setState(next);
  }, [_setState, state]);
  useEffect(() => {
    if (nextPendingStepRef.current == null) {
      return;
    }
    if (deepEqual(nextStateRef.current, state)) {
      setStep(nextPendingStepRef.current);
      nextPendingStepRef.current = null;
    }
  }, [setStep, state]);
  const initializedRef = useRef(false);
  function withState<State extends Record<string, unknown>>(initialState: State) {
    if (!initializedRef.current) {
      setState(initialState);
      initializedRef.current = true;
    }
    return [FunnelComponent, state, setState] as const;
  }
  return Object.assign([FunnelComponent, setStep] as const, {
    withState
  }) as readonly [FunnelComponent<Steps>, (step: Steps[number], options?: SetStepOptions) => void] & {
    withState: <StateExcludeStep extends Record<string, unknown>>(initialState: StateExcludeStep) => [FunnelComponent<Steps>, StateExcludeStep, (next: Partial<StateExcludeStep & {
      step: Steps[number];
    }> | ((next: Partial<StateExcludeStep & {
      step: Steps[number];
    }>) => StateExcludeStep & {
      step: Steps[number];
    })) => void];
  };
};